import { useMemo, useState, type ReactNode } from 'react';
import { color, redcapBoardTheme } from '../styles/theme';
import { dateTime, fullDate, num, plainDate, shortDate } from '../lib/format';
import { makeLookup, summaryOf } from '../lib/presenters';
import {
  boardColumns,
  advanceStatus,
  studySlugOf,
  isInbox,
  INBOX_STATUSES,
  groupDot,
  statusLabelOf,
  endorsable,
  ENDORSED,
} from '../lib/pipeline';
import { useMeta, useSubmissions, useSetStatus } from '../api/queries';
import { useEndorse, useEndorsedOut, isEndpointMissing } from '../api/endorsements';
import { useRedcapBoard } from '../api/redcap';
import type { EndorsedOut, MetaResponse, RedcapBoardResponse, SubmissionRow } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageTitle, Card, Confirm, Loading, ErrorNote, RedactedBadge, Button, Empty } from '../components/ui';
import { PreviewPill } from '../components/pills';
import { EndorseControl } from '../components/EndorseControl';
import type { RedcapTab } from './Redcap';
import { ScrollRow } from '../components/ScrollRow';
import { ParticipantDetail } from './ParticipantDetail';

/**
 * One study's board.
 *
 * A participant is someone accepted into this study from Forms submission, so
 * the funnel columns start at Intake scheduled and run to the closed bucket,
 * with Hold beside them. Received, Waitlist and Early interest are not here —
 * they are the inbox's, and nobody in them is a participant yet.
 *
 * Two columns bracket the funnel, for people moving between studies. Newly
 * endorsed, first, is whoever another study's team handed to this one: they
 * wait there until this team accepts them. Endorsed out, last, is whoever
 * this team handed on — no longer this study's record, kept in view so the
 * team can see the hand-off landed. The first is a status on the row; the
 * second is the API's record of the hand-off, because once someone has moved
 * nothing on their row says they were ever here. Every card carries the
 * Endorse to… control that starts a hand-off, parked and closed ones too: a
 * "not a match" here is often exactly who another study wants.
 *
 * The board pulls whole form lists rather than a page at a time: a kanban
 * needs every card to place it in a column, and paging would hide whichever
 * stage happened to fall past the page boundary. The API's `study` filter
 * keeps each request to this study's rows, so the 100-row ceiling is per
 * study per form; the header still says so if one is ever up against it.
 */
const BOARD_PAGE_SIZE = 100;

/** The one question open on the board: a card's next step, or its Endorse to a study. */
type Pending = { id: string; action: 'next' } | { id: string; action: 'endorse'; study: string };

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: color.muted,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 13.5, fontWeight: 800, color: color.ink, whiteSpace: 'nowrap' }}>{value}</span>
    </span>
  );
}

/**
 * The study's REDCap project, as the board sees it: which project, whether its
 * dictionary is on file, what came back from it, and how many people on this
 * board have gone out in an export. The upload into REDCap is by hand, so
 * "exported" is what this CRM knows — that the sign-up was in a file.
 *
 * Deliberately not whether a card is linked to a REDCap record: that link is
 * PHI, gated per person, and this board is open to every coordinator.
 */
function RedcapStrip({
  board,
  onBoard,
  exportedHere,
  onOpen,
  canSetUp,
}: {
  board: RedcapBoardResponse;
  onBoard: number;
  exportedHere: number;
  onOpen?: (tab: RedcapTab) => void;
  canSetUp: boolean;
}) {
  const p = board.project;
  const small = { padding: '7px 13px', fontSize: 12.5 } as const;

  if (!p) {
    return (
      <Card pad="14px 18px" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: color.muted, flex: 1, minWidth: 240 }}>
          This study is not linked to its REDCap project yet, so nobody on the board can be exported to it.
        </span>
        {onOpen && canSetUp && (
          <Button variant="soft" onClick={() => onOpen('setup')} style={small}>
            Set up REDCap
          </Button>
        )}
      </Card>
    );
  }

  return (
    <Card pad="14px 18px" style={{ display: 'flex', alignItems: 'center', gap: '12px 26px', flexWrap: 'wrap' }}>
      <Fact label="REDCap project" value={`PID ${p.pid}`} />
      <Fact
        label="Data dictionary"
        value={
          p.dictionaryFields > 0
            ? `${num(p.dictionaryFields)} fields${p.dictionaryUploadedAt ? ` · ${shortDate(p.dictionaryUploadedAt)}` : ''}`
            : 'Not uploaded'
        }
      />
      <Fact label="Exported from this board" value={`${num(exportedHere)} of ${num(onBoard)}`} />
      <Fact label="Records imported" value={num(p.records)} />
      <Fact label="Weekly extract" value={p.lastAsOf ? `Week of ${plainDate(p.lastAsOf)}` : 'None yet'} />
      <Fact label="Open chases" value={num(p.openChases)} />
      {onOpen && (
        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginLeft: 'auto' }}>
          <Button variant="soft" onClick={() => onOpen('export')} style={small}>
            Export to REDCap
          </Button>
          <Button variant="ghost" onClick={() => onOpen('completeness')} style={small}>
            This week
          </Button>
          <Button variant="ghost" onClick={() => onOpen(p.dictionaryFields > 0 ? 'analytics' : 'setup')} style={small}>
            {p.dictionaryFields > 0 ? 'Analytics' : 'Project setup'}
          </Button>
        </span>
      )}
    </Card>
  );
}

/**
 * What Participants says when there is no board to show — App renders it when
 * `/meta` lists no study for this person. Two different reasons: nobody has
 * made a study yet, or, for anyone but an owner, none has been given to them,
 * which they cannot change themselves and should not be sent to Studies for.
 */
export function NoBoards() {
  const { admin, can } = useAuth();
  if (admin && !admin.allStudies) {
    return (
      <Empty>
        No study has been given to you yet, so there is no board to show. An owner can give you one under Admin
        access → User management → Study access.
      </Empty>
    );
  }
  return (
    <Empty>
      {can('studies:manage')
        ? 'No studies yet. Create one under Studies, and its board appears here.'
        : 'No studies yet. When one is made under Studies, its board appears here.'}
    </Empty>
  );
}

export function Participants({
  study,
  onOpenRedcap,
}: {
  study: string;
  /** Open the REDCap section on this study, at a tab. */
  onOpenRedcap?: (study: string, tab: RedcapTab) => void;
}) {
  const meta = useMeta();
  const { can } = useAuth();
  const setStatus = useSetStatus();
  const redcap = useRedcapBoard(study);
  /** Sign-up id → when it last went out in an export file. */
  const exportedAt = useMemo(
    () => new Map((redcap.data?.exported ?? []).map((e) => [e.submissionId, e.exportedAt])),
    [redcap.data],
  );
  const endorse = useEndorse();
  const [selected, setSelected] = useState<string | null>(null);
  // The card whose move or hand-off is waiting to be confirmed — one at a
  // time, so the board never has two open questions on it.
  const [confirming, setConfirming] = useState<Pending | null>(null);

  // Only the statuses a board shows. Without this the newest 100 rows of a form
  // could all be people still waiting in Forms submission, dropped below, and an
  // accepted participant would fall off the page (screen QA, 20 Oct 2026).
  const boardStatuses = meta.data?.statuses.map((s) => s.key).filter((k) => !INBOX_STATUSES.includes(k));
  const base = {
    status: 'all',
    q: '',
    sort: 'newest' as const,
    page: 1,
    pageSize: BOARD_PAGE_SIZE,
    study,
    ...(boardStatuses ? { statuses: boardStatuses.join(',') } : {}),
  };
  const intake = useSubmissions({ ...base, tab: 'intake' });
  const referral = useSubmissions({ ...base, tab: 'referral' });
  const waitlist = useSubmissions({ ...base, tab: 'waitlist' });
  const interest = useSubmissions({ ...base, tab: 'early-interest' });
  const lists = [intake, referral, waitlist, interest];
  // Asked for only once `/meta` says the API records endorsements. An older
  // API has neither the status nor the route, and both columns say so.
  const recordsEndorsements = !!meta.data && endorsable(meta.data);
  const out = useEndorsedOut(study, recordsEndorsements);

  if (meta.isLoading || lists.some((l) => l.isLoading && !l.data)) return <Loading />;
  const failed = lists.find((l) => l.error);
  if (failed) return <ErrorNote message={(failed.error as Error).message} />;
  if (!meta.data) return null;

  const L = makeLookup(meta.data);
  const board = boardColumns(meta.data);
  // The API already limits each list to this study; the check here only
  // matters against an older API without the filter.
  const onBoard: SubmissionRow[] = lists
    .flatMap((l) => l.data?.rows ?? [])
    .filter((r) => !isInbox(r) && studySlugOf(r) === study);
  // Newly endorsed people are this study's record but not yet its
  // participants: nobody here has accepted them.
  const incoming = onBoard.filter((r) => r.status === ENDORSED);
  const rows = onBoard.filter((r) => r.status !== ENDORSED);
  const truncated = lists.some((l) => l.data && l.data.total > l.data.rows.length);
  const canUpdate = can('submissions:update');

  const columns = board.map((col) => ({
    ...col,
    cards: rows.filter((r) => col.statuses.includes(r.status)),
  }));

  const endorsedStatus = meta.data.statuses.find((s) => s.key === ENDORSED);
  const outPreview = !recordsEndorsements || (!!out.error && isEndpointMissing(out.error));
  const outCards = out.data?.endorsements ?? [];

  function move(row: SubmissionRow, status: string) {
    setConfirming(null);
    setStatus.mutate({ id: row.id, status });
  }

  // No note on success: the card leaves its column and comes back under
  // Endorsed out, which is the trace this board keeps of a hand-off.
  function handOver(row: SubmissionRow, study: string) {
    setConfirming(null);
    endorse.mutate({ id: row.id, study });
  }

  const label = L.studyLabel(study);

  function card(r: SubmissionRow, extra?: ReactNode) {
    const fm = meta.data!.forms[r.form];
    const next = advanceStatus(meta.data!, r);
    const accepting = r.status === ENDORSED;
    const moveOpen = confirming?.id === r.id && confirming.action === 'next';
    const endorseTo =
      confirming?.id === r.id && confirming.action === 'endorse' ? confirming.study : null;

    return (
      <ParticipantCard
        key={r.id}
        name={r.name}
        date={shortDate(r.createdAt)}
        form={fm.short}
        redacted={r.redacted}
        summary={summaryOf(r, L)}
        exportedAt={exportedAt.get(r.id)}
        onOpen={() => setSelected(r.id)}
        extra={extra}
      >
        {canUpdate && (
          /*
            Confirmed, not fired on the first click. These controls sit
            inside a card that is itself clickable, one column over
            from the next status, and the status they set is what the
            participant is told about their own application — so the
            bubble states the move and quotes the line they will be
            shown before anything is sent. The wrapper keeps their clicks
            and keys from opening the record: a select's bubble up too.
          */
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, maxWidth: '100%' }}
          >
            {next && (
              <Confirm
                open={moveOpen}
                title={
                  accepting
                    ? `Accept ${r.name} into ${L.studyShort(study)}?`
                    : `Move ${r.name} to ${next.label}?`
                }
                detail={
                  accepting
                    ? next.blurb
                      ? `They move to ${next.label}, and will be shown: “${next.blurb}”`
                      : `They move to ${next.label}.`
                    : next.blurb
                      ? `They will be shown: “${next.blurb}”`
                      : undefined
                }
                confirmLabel={accepting ? 'Accept' : 'Move'}
                onConfirm={() => move(r, next.key)}
                onCancel={() => setConfirming(null)}
              >
                <button
                  onClick={() => setConfirming(moveOpen ? null : { id: r.id, action: 'next' })}
                  disabled={setStatus.isPending}
                  className="hov-tint"
                  style={{
                    background: moveOpen ? color.tint : 'transparent',
                    border: `1px solid ${color.blue}`,
                    color: color.blue,
                    fontSize: 11.5,
                    fontWeight: 700,
                    padding: '6px 13px',
                    borderRadius: 999,
                    cursor: 'pointer',
                  }}
                >
                  {/* The card is narrow; the bubble says where accepting moves them. */}
                  {accepting ? 'Accept →' : `Move to ${next.label} →`}
                </button>
              </Confirm>
            )}
            <EndorseControl
              row={r}
              meta={meta.data!}
              size="pill"
              target={endorseTo}
              onTarget={(s) => setConfirming(s ? { id: r.id, action: 'endorse', study: s } : null)}
              onEndorse={(study) => handOver(r, study)}
              pending={endorse.isPending || setStatus.isPending}
            />
          </div>
        )}
      </ParticipantCard>
    );
  }

  const subtitle = [
    `${rows.length} ${rows.length === 1 ? 'participant' : 'participants'}`,
    incoming.length ? `${incoming.length} newly endorsed` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} className="fade-in">
      <PageTitle
        sub={`${board
          .filter((c) => c.group === 'open')
          .map((c) => c.label)
          .join(' → ')} — ${subtitle}.`}
      >
        {label === '—' ? 'Participants' : label}
      </PageTitle>

      {redcap.data && (
        <RedcapStrip
          board={redcap.data}
          onBoard={rows.length}
          exportedHere={rows.filter((r) => exportedAt.has(r.id)).length}
          onOpen={onOpenRedcap ? (tab) => onOpenRedcap(study, tab) : undefined}
          canSetUp={can('redcap:import')}
        />
      )}

      {truncated && (
        <ErrorNote
          message={`Showing the first ${BOARD_PAGE_SIZE} of each form for this study, so the board may be missing people.`}
        />
      )}

      {rows.length === 0 && incoming.length === 0 && outCards.length === 0 && (
        <Card pad={24}>
          <p style={{ fontSize: 13.5, color: color.muted, margin: 0 }}>
            No one has been accepted into this study yet. Forms submission is where they start, or
            another study can endorse someone here.
          </p>
        </Card>
      )}

      {/*
        One row, always. The columns read as one funnel left to right —
        arrivals from other studies first, hand-offs to them last — and
        letting Hold and Closed wrap underneath made them look like a second
        board. When there is room the columns share the width; when there
        is not, each takes a comfortable width and the row scrolls
        sideways, with the scrollbar above the headings rather than below
        the longest column. Never a squeeze in between.
      */}
      <ScrollRow>
        <Column
          label={endorsedStatus?.label ?? 'Newly endorsed'}
          title="Endorsed here by another study's team. Accept them to start intake."
          dot={groupDot.open}
          count={incoming.length}
          preview={!recordsEndorsements}
        >
          {!recordsEndorsements ? (
            <ColumnNote>Needs the API update that records endorsements.</ColumnNote>
          ) : (
            <>
              {incoming.map((r) =>
                card(
                  r,
                  r.endorsedFrom ? (
                    <FromTo dir="From" study={r.endorsedFrom} at={r.endorsedFrom.at} />
                  ) : null,
                ),
              )}
              {incoming.length === 0 && <ColumnNote>No one endorsed here right now.</ColumnNote>}
            </>
          )}
        </Column>

        {columns.map((col) => (
          <Column
            key={col.key}
            label={col.label}
            title={col.blurb}
            dot={groupDot[col.group]}
            count={col.cards.length}
          >
            {col.cards.map((r) => card(r))}
            {col.cards.length === 0 && <ColumnNote>No one here right now.</ColumnNote>}
          </Column>
        ))}

        <Column
          label="Endorsed out"
          title="Endorsed to another study. They are that study's record now; this is where they went."
          dot={groupDot.closed}
          count={out.data?.total ?? outCards.length}
          preview={outPreview}
        >
          {outPreview ? (
            <ColumnNote>Needs the API update that records endorsements.</ColumnNote>
          ) : out.isLoading ? (
            <ColumnNote>Loading…</ColumnNote>
          ) : out.error ? (
            <ErrorNote message={(out.error as Error).message} />
          ) : (
            <>
              {outCards.map((e) => (
                <EndorsedOutCard
                  key={e.id}
                  e={e}
                  meta={meta.data!}
                  onOpen={() => setSelected(e.submissionId)}
                />
              ))}
              {outCards.length === 0 && <ColumnNote>No one endorsed to another study.</ColumnNote>}
              {out.data && out.data.total > outCards.length && (
                <ColumnNote>
                  The latest {outCards.length} of {out.data.total}.
                </ColumnNote>
              )}
            </>
          )}
        </Column>
      </ScrollRow>

      {(setStatus.error || endorse.error) && (
        <ErrorNote message={((setStatus.error ?? endorse.error) as Error).message} />
      )}

      {selected && <ParticipantDetail id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

/** A column's frame: the heading with its dot and count, then the cards. */
function Column({
  label,
  title,
  dot,
  count,
  preview,
  children,
}: {
  label: string;
  title?: string;
  dot: string;
  count: number;
  /** The API has not got the routes this column reads; the count would be a guess. */
  preview?: boolean;
  children: ReactNode;
}) {
  return (
    <Card pad={14} style={{ borderRadius: 18, display: 'grid', gap: 10, flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '2px 4px' }}>
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: dot,
            flex: 'none',
          }}
        />
        <span
          title={title}
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: color.ink,
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </span>
        {preview ? (
          <PreviewPill>Preview</PreviewPill>
        ) : (
          <span
            style={{
              background: color.tint,
              color: color.blueDeep,
              fontSize: 11.5,
              fontWeight: 800,
              minWidth: 22,
              height: 22,
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 7px',
              flex: 'none',
            }}
          >
            {count}
          </span>
        )}
      </div>
      {children}
    </Card>
  );
}

function ColumnNote({ children }: { children: ReactNode }) {
  return (
    <p style={{ fontSize: 12, color: color.muted, margin: 0, padding: '6px 4px 8px' }}>{children}</p>
  );
}

/**
 * One person on the board.
 *
 * Opens the record when the card can be opened — `onOpen` absent means it
 * cannot (someone endorsed to a study this person does not work in), and the
 * card is then plain text rather than a button that answers "not found".
 */
function ParticipantCard({
  name,
  date,
  dateTitle,
  form,
  redacted,
  summary,
  exportedAt,
  extra,
  onOpen,
  closedTitle,
  children,
}: {
  name: string;
  date: string;
  dateTitle?: string;
  form: string;
  redacted: boolean;
  summary?: string;
  /** When this sign-up last went out in a REDCap import file, if it has. */
  exportedAt?: string;
  extra?: ReactNode;
  onOpen?: () => void;
  /** Said on hover when there is nothing to open. */
  closedTitle?: string;
  children?: ReactNode;
}) {
  return (
    <div
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      title={onOpen ? undefined : closedTitle}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      className={onOpen ? 'hov-border-blue' : undefined}
      style={{
        background: color.bg,
        border: `1px solid ${color.line}`,
        borderRadius: 14,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        cursor: onOpen ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            fontSize: 13.5,
            fontWeight: 800,
            color: color.ink,
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </span>
        <span title={dateTitle} style={{ fontSize: 11, color: color.muted, flex: 'none' }}>
          {date}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 11.5,
            color: color.muted,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {form}
        </span>
        {redacted && <RedactedBadge />}
        {exportedAt && (
          <span
            title={`In a REDCap import file made ${dateTime(exportedAt)}. The upload into REDCap itself is done by hand.`}
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              padding: '3px 8px',
              borderRadius: 999,
              background: redcapBoardTheme.exported.bg,
              color: redcapBoardTheme.exported.fg,
              whiteSpace: 'nowrap',
            }}
          >
            Exported · {shortDate(exportedAt)}
          </span>
        )}
      </div>

      {summary && (
        <p style={{ fontSize: 12, lineHeight: '17px', color: color.muted, margin: 0 }}>{summary}</p>
      )}

      {extra}

      {children}
    </div>
  );
}

/**
 * The other end of a hand-off: "From OUD · Sep 20" on a card that arrived,
 * "To EMBRACE" on one that left. The study is named by the API rather than
 * looked up in `/meta`, which lists only the caller's own studies.
 */
function FromTo({
  dir,
  study,
  at,
  children,
}: {
  dir: 'From' | 'To';
  study: { short: string; label: string };
  at?: string;
  children?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', minWidth: 0 }}>
      <span
        title={`${dir} ${study.label}`}
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          background: color.tint2,
          color: color.blueDeep,
          fontSize: 10.5,
          fontWeight: 800,
          padding: '3px 10px',
          borderRadius: 999,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {dir} {study.short}
      </span>
      {at && (
        <span title={fullDate(at)} style={{ fontSize: 11.5, color: color.muted }}>
          {shortDate(at)}
        </span>
      )}
      {children}
    </div>
  );
}

/**
 * Someone this study endorsed to another. No action: they are the other
 * team's now. The card says where they went and how it landed — waiting to
 * be accepted, where they are on that board, or that they have been passed
 * on again — and opens the record only for someone who works on the study
 * the person is in today.
 */
function EndorsedOutCard({
  e,
  meta,
  onOpen,
}: {
  e: EndorsedOut;
  meta: MetaResponse;
  onOpen: () => void;
}) {
  const where = e.now;
  const reachable = !!where && meta.studies.some((s) => s.key === where.key);
  const landed = !where
    ? 'No study now'
    : where.key !== e.to.key
      ? `Now with ${where.short}`
      : e.status === ENDORSED
        ? 'Not accepted yet'
        : statusLabelOf(meta, e.status);

  return (
    <ParticipantCard
      name={e.name}
      date={shortDate(e.endorsedAt)}
      dateTitle={`Endorsed ${fullDate(e.endorsedAt)}${e.endorsedBy ? ` by ${e.endorsedBy}` : ''}`}
      form={meta.forms[e.form].short}
      redacted={e.redacted}
      summary={`Left from ${statusLabelOf(meta, e.fromStatus)}`}
      extra={
        <FromTo dir="To" study={e.to}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: color.muted }}>{landed}</span>
        </FromTo>
      }
      onOpen={reachable ? onOpen : undefined}
      closedTitle={
        where ? `${where.label}'s record now. You do not work on that study.` : undefined
      }
    />
  );
}
